import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const KEY = "sfushop-cart-v2";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem(KEY) || "[]"));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => {
        const byId = new Map((Array.isArray(data) ? data : []).map((p) => [p.id, p]));
        setItems((prev) =>
          prev.flatMap((i) => {
            const product = byId.get(i.product_id);
            const variant = product?.variants?.find((v) => v.id === (i.variant_id ?? i.size));
            return variant ? [{ ...i, name: product.name, price: product.price, image: product.image, slug: product.slug, size: variant.title }] : [];
          })
        );
      })
      .catch(() => {});
  }, []);

  const add = (product, variant) =>
    setItems((prev) => {
      const key = `${product.id}-${variant.id}`;
      if (prev.some((i) => i.key === key)) {
        return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + 1, 10) } : i));
      }
      const variantId = product.source === "gelato" ? { variant_id: variant.id } : {};
      return [...prev, { key, product_id: product.id, ...variantId, slug: product.slug, name: product.name, price: product.price, image: product.image, size: variant.title, qty: 1 }];
    });

  const setQty = (key, qty) =>
    setItems((prev) => (qty < 1 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty: Math.min(qty, 10) } : i))));

  const value = useMemo(
    () => ({
      items,
      open,
      setOpen,
      add,
      setQty,
      clear: () => setItems([]),
      count: items.reduce((s, i) => s + i.qty, 0),
      subtotal: items.reduce((s, i) => s + i.qty * i.price, 0),
    }),
    [items, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
